import { FunctionComponent, memo, PropsWithChildren, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useDialogFocus } from '../../hooks';
import { isModalOpened } from '../../utils';

import classNames from 'classnames';
import classes from './ModalSlidePanel.module.scss';

export enum PanelSide {
  left = 'left',
  right = 'right',
}

interface ModalSlidePanelProps {
  isOpen: boolean;
  body: JSX.Element;
  onClose: () => void;
  ariaLabel: string;
  side?: PanelSide;
  root?: string;
}

export const ModalSlidePanel: FunctionComponent<PropsWithChildren<ModalSlidePanelProps>> = memo(
  ({ isOpen, body, onClose, ariaLabel, side = PanelSide.right, root = 'modal-root' }) => {
    const location = useLocation();

    const dialogRef = useRef<HTMLDivElement>(null);

    const bodyRef = useRef<HTMLDivElement>(null);

    useDialogFocus({
      dialogRef,
      isOpen,
      onEscape: onClose,
      initialFocusRef: bodyRef,
      isModal: true,
    });

    useEffect(() => {
      if (isOpen) {
        onClose();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      }

      return () => {
        if (!isModalOpened()) {
          document.body.style.overflow = 'auto';
        }
      };
    }, [isOpen]);

    return ReactDOM.createPortal(
      <div
        ref={dialogRef}
        className={classNames(classes['panel'], { [classes['panel--visible']]: isOpen })}
        role={'dialog'}
        aria-label={ariaLabel}
      >
        <div
          className={classNames(classes['panel__overlay'], {
            [classes['panel__overlay--visible']]: isOpen,
          })}
          onClick={onClose}
        ></div>
        <div
          ref={bodyRef}
          className={classNames(classes['panel__body'], {
            [classes['panel__body--visible']]: isOpen,
            [classes[`panel__body--${side}`]]: side,
          })}
          tabIndex={-1}
        >
          {body}
        </div>
      </div>,
      document.getElementById(root) as HTMLElement
    );
  }
);
